import React, { useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import { PackageTable, PackageCards } from "@/components/pricing/RealEstatePackages";
import SEO, { getServiceSchema, getBreadcrumbSchema } from '../../components/SEO';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * REAL ESTATE PRICING PAGE - /pricing/real-estate
 * Contains packages and individual services for real estate media
 * ═══════════════════════════════════════════════════════════════════════════
 */


const PricingRealEstate = () => {
  const location = useLocation();

  // Handle hash scroll on page load
  useEffect(() => {
    if (location.hash) {
      const scrollToHash = () => {
        const element = document.querySelector(location.hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      };
      
      if (document.readyState === 'complete') {
        setTimeout(scrollToHash, 100);
      } else {
        window.addEventListener('load', () => setTimeout(scrollToHash, 100), { once: true });
      }
      
      const backupTimer = setTimeout(scrollToHash, 800);
      return () => clearTimeout(backupTimer);
    }
  }, [location.hash]);

  return (
    <div className="flex flex-col min-h-screen">
      {/* SEO Meta Tags */}
      <SEO
        title="Real Estate Photography Packages from $199"
        description="Professional real estate photography starting at $199. HDR photos, drone aerials, 24-hour delivery. Serving Kingman, Bullhead City & Lake Havasu, Arizona."
        path="/pricing/real-estate"
        jsonLd={[
          getServiceSchema({
            name: 'Real Estate Photography',
            description: 'Professional real estate photography, video tours, drone media, and virtual staging services for property listings.',
            url: 'https://rcstudios.net/pricing/real-estate',
            areaServed: 'Arizona',
            priceRange: '$199-$749'
          }),
          getBreadcrumbSchema([
            { name: 'Home', path: '/' },
            { name: 'Pricing', path: '/pricing' },
            { name: 'Real Estate', path: '/pricing/real-estate' }
          ])
        ]}
      />

      {/* Hero Section */}
      <section className="bg-[#050816] text-white pt-32 pb-20">
        <div className="container mx-auto px-6 max-w-[1000px]">
          {/* Back Link */}
          <Link 
            to="/pricing" 
            className="inline-flex items-center gap-2 text-gray-400 hover:text-white text-sm font-medium mb-8 transition-colors"
            data-testid="back-to-categories"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Categories
          </Link>
          
          <div className="text-center">
            <p className="text-[#F5C056] text-xs font-bold uppercase tracking-[0.2em] mb-4">Real Estate Media</p>
            <h1 className="font-serif font-semibold text-4xl md:text-5xl mb-6">
              Real Estate Visual Packages
            </h1>
            <p className="text-lg text-gray-300 max-w-2xl mx-auto">
              Professional photography and media solutions designed to showcase properties at their best.
            </p>
          </div>
        </div>
      </section>

      {/* Packages Section - Comparison Table */}
      <section id="packages" className="py-20 bg-[#FAF9F6] scroll-mt-24">
        <div className="container mx-auto px-6 max-w-[1100px]">
          {/* Section Header */}
          <div className="text-center mb-12">
            <h2 className="font-serif text-3xl md:text-4xl text-gray-900 mb-4">Choose Your Package</h2>
            <p className="text-gray-600 text-base max-w-xl mx-auto">
              All packages include 24-hour delivery. Creator Spotlight covers homes of any size.
            </p>
          </div>

          <PackageTable />
          <PackageCards />
        </div>
      </section>

      {/* Individual Services Section */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-6 max-w-[1200px]">
          {/* Section Header */}
          <div className="text-center mb-16 space-y-4">
            <h2 className="font-serif text-4xl md:text-5xl text-foreground">Individual Services</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Don&apos;t need a full package? Build your own custom order with our a la carte options.
            </p>
          </div>

          {/* Service 1: Realsee 3D Tour */}
          <div id="realsee-3d" className="grid lg:grid-cols-12 gap-8 items-start mb-24 scroll-mt-24">
            {/* Left: Image Card */}
            <div className="lg:col-span-5 relative group rounded-2xl overflow-hidden shadow-xl aspect-[4/3] transition-all duration-500 ease-out hover:-translate-y-1.5 hover:shadow-2xl">
              <img
                src="https://images.unsplash.com/photo-1487958449943-2429e8be8625?auto=format&fit=crop&q=80&w=800"
                alt="Realsee 3D Tour"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#050816]/90 via-[#050816]/30 to-transparent"></div>
              <div className="absolute bottom-0 left-0 p-8 text-white">
                <h3 className="font-serif text-2xl font-bold mb-2">Immersive Spatial Capture</h3>
                <p className="text-white/90 text-sm leading-relaxed font-sans">Precision 3D scanning for next-generation property experiences.</p>
              </div>
            </div>

            {/* Right: Detail Card */}
            <div className="lg:col-span-7 bg-[#FAF9F6] rounded-2xl p-8 pb-10 shadow-lg border border-gray-100 transition-all duration-500 ease-out hover:-translate-y-1.5 hover:shadow-xl hover:border-[#F5C056]/40 group h-auto overflow-visible relative z-10">
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
                <div>
                  <div className="flex items-center gap-3 mb-1">
                    <h3 className="font-serif text-2xl font-bold text-gray-900">Realsee 3D Tour (Galois M2 — 134 MP)</h3>
                  </div>
                  <p className="text-sm text-muted-foreground max-w-md">High-resolution scan for immersive virtual tours and premium marketing assets.</p>
                </div>
                <div className="text-right shrink-0">
                  <span className="inline-block bg-[#F5C056] text-[#050816] text-sm font-bold uppercase tracking-wider px-4 py-2 rounded-full">Coming Soon</span>
                </div>
              </div>

              <div className="h-px bg-gray-200 w-full mb-8"></div>

              <div className="grid md:grid-cols-2 gap-y-4 gap-x-8 mb-10">
                {[
                  "134 MP ultra-high-resolution capture",
                  "Immersive 3D virtual tour",
                  "16K panorama outputs",
                  "LiDAR depth + spatial accuracy",
                  "Floor plan option",
                  "Branded tour + labels/annotations",
                  "Downloadable panoramas",
                  "Share + embed ready (website/social)"
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3 group/item">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#F5C056] mt-2 group-hover/item:bg-[#F5C056] transition-colors"></div>
                    <span className="text-gray-600 text-[13px] leading-relaxed group-hover/item:text-gray-900 transition-colors">{item}</span>
                  </div>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row gap-4 pt-2">
                <Button className="bg-[#050816] text-white hover:bg-[#050816]/90 rounded-lg h-11 px-8 text-xs uppercase tracking-widest font-medium transition-all shadow-md hover:shadow-lg">
                  Get a quote
                </Button>
                <Button variant="outline" className="border-gray-300 text-gray-700 hover:bg-white hover:text-gray-900 hover:border-gray-400 rounded-lg h-11 px-8 text-xs uppercase tracking-widest font-medium transition-all bg-transparent">
                  Learn more
                </Button>
              </div>
            </div>
          </div>

          {/* Service 2: Cinematic Video Tours */}
          <div id="video-tours" className="grid lg:grid-cols-12 gap-8 items-start mb-24 scroll-mt-24">
            {/* Left: Detail Card */}
            <div className="lg:col-span-7 bg-[#FAF9F6] rounded-2xl p-8 shadow-lg border border-gray-100 order-2 lg:order-1">
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
                <div>
                  <h3 className="text-2xl font-bold text-foreground">Cinematic Video Tours</h3>
                  <p className="text-muted-foreground">Immersive video experiences that sell.</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-muted-foreground">Starting at</p>
                  <p className="text-3xl font-bold text-foreground">$250</p>
                </div>
              </div>

              <div className="h-px bg-gray-200 w-full mb-6"></div>

              <div className="grid md:grid-cols-2 gap-4 mb-8">
                {["4K resolution with gimbal stabilization", "Licensed music & professional voiceover", "Social-optimized edits included", "MLS-compliant format"].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#F5C056]"></div>
                    <span className="text-gray-600 text-sm">{item}</span>
                  </div>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <Button className="bg-[#050816] text-white hover:bg-[#050816]/90 rounded-lg py-4 px-8 text-base">
                  Get a quote
                </Button>
                <Button variant="outline" className="border-[#050816]/20 text-foreground hover:bg-gray-100 rounded-lg py-4 px-8 text-base">
                  Learn more
                </Button>
              </div>
            </div>

            {/* Right: Image Card */}
            <div className="lg:col-span-5 relative group rounded-2xl overflow-hidden shadow-xl aspect-[4/3] order-1 lg:order-2">
              <img
                src="https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&q=80&w=800"
                alt="Cinematic Video Preview"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>
              <div className="absolute bottom-0 left-0 p-8 text-white">
                <h3 className="text-2xl font-bold mb-2">Video Production</h3>
                <p className="text-white/90">High-end video content for social and web.</p>
              </div>
            </div>
          </div>

          {/* Service 3: Professional Floor & Site Plans */}
          <div id="floor-plans" className="grid lg:grid-cols-12 gap-8 items-start mb-24 scroll-mt-24">
            {/* Left: Image Card */}
            <div className="lg:col-span-5 relative group rounded-2xl overflow-hidden shadow-xl aspect-[4/3]">
              <img
                src="https://customer-assets.emergentagent.com/job_page-builder-81/artifacts/kxchah33_FLoOR-plans.jpg"
                alt="Floor Plan Preview"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>
              <div className="absolute bottom-0 left-0 p-8 text-white">
                <h3 className="text-2xl font-bold mb-2">Schematic Plans</h3>
                <p className="text-white/90">Accurate layouts for every listing.</p>
              </div>
            </div>

            {/* Right: Detail Card */}
            <div className="lg:col-span-7 bg-[#FAF9F6] rounded-2xl p-8 shadow-lg border border-gray-100">
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
                <div>
                  <h3 className="text-2xl font-bold text-foreground">Professional Floor & Site Plans</h3>
                  <p className="text-muted-foreground">Clear, accurate 2D layouts to help buyers visualize the space.</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-muted-foreground">Starting at</p>
                  <p className="text-3xl font-bold text-foreground">$100</p>
                </div>
              </div>

              <div className="h-px bg-gray-200 w-full mb-6"></div>

              <div className="grid md:grid-cols-2 gap-4 mb-8">
                {["Site Plan", "Redraw Existing Plans", "Interactive Floor Plan", "Custom Branding"].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#F5C056]"></div>
                    <span className="text-gray-600 text-sm">{item}</span>
                  </div>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <Button className="bg-[#050816] text-white hover:bg-[#050816]/90 rounded-lg py-4 px-8 text-base">
                  Get a quote
                </Button>
                <Button variant="outline" className="border-[#050816]/20 text-foreground hover:bg-gray-100 rounded-lg py-4 px-8 text-base">
                  Learn more
                </Button>
              </div>
            </div>
          </div>

          {/* Service 4: Aerial Photos & Video */}
          <div id="aerial" className="grid lg:grid-cols-12 gap-8 items-start scroll-mt-24">
            {/* Left: Detail Card */}
            <div className="lg:col-span-7 bg-[#FAF9F6] rounded-2xl p-8 shadow-lg border border-gray-100 order-2 lg:order-1">
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
                <div>
                  <h3 className="text-2xl font-bold text-foreground">Aerial Photos & Video</h3>
                  <p className="text-muted-foreground">Showcase the property from a new perspective.</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-muted-foreground">Starting at</p>
                  <p className="text-3xl font-bold text-foreground">$250</p>
                </div>
              </div>

              <div className="h-px bg-gray-200 w-full mb-6"></div>

              <div className="grid md:grid-cols-2 gap-4 mb-8">
                {["Aerial Twilight", "Aerial Photo Add-On", "Aerial Video Add-On", "Community Highlights"].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#F5C056]"></div>
                    <span className="text-gray-600 text-sm">{item}</span>
                  </div>
                ))}
              </div>

              <div className="p-4 bg-white rounded-lg border border-gray-100 mb-8">
                <p className="text-sm text-muted-foreground">
                  <strong>Note:</strong> Drone operations are subject to weather conditions.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <Button className="bg-[#050816] text-white hover:bg-[#050816]/90 rounded-lg py-4 px-8 text-base">
                  Get a quote
                </Button>
                <Button variant="outline" className="border-[#050816]/20 text-foreground hover:bg-gray-100 rounded-lg py-4 px-8 text-base">
                  Learn more
                </Button>
              </div>
            </div>

            {/* Right: Image Card */}
            <div className="lg:col-span-5 relative group rounded-2xl overflow-hidden shadow-xl aspect-[4/3] order-1 lg:order-2">
              <img
                src="https://customer-assets.emergentagent.com/job_page-builder-81/artifacts/g37qj7be_drone.jpg"
                alt="Aerial Drone Preview"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>
              <div className="absolute bottom-0 left-0 p-8 text-white">
                <h3 className="text-2xl font-bold mb-2">Drone Services</h3>
                <p className="text-white/90">Fully insured pilots and stunning 4K quality.</p>
              </div>
            </div>
          </div>

          {/* Service 5: Virtual Staging & Renovation */}
          <div id="virtual-staging-renovation" className="mt-24 pt-16 border-t border-gray-100 scroll-mt-24">
            {/* Section Header */}
            <div className="text-center mb-12 space-y-4">
              <h3 className="font-serif text-3xl md:text-4xl text-foreground">Virtual Staging & Renovation</h3>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Help buyers see a property&apos;s true potential with realistic visualization of cosmetic updates.
              </p>
            </div>

            {/* Before/After Image Comparison */}
            <div className="grid md:grid-cols-2 gap-6 mb-12 max-w-[900px] mx-auto">
              <div className="relative rounded-xl overflow-hidden shadow-lg group">
                <img
                  src="https://customer-assets.emergentagent.com/job_rcstudioapp/artifacts/mrn9cn8n_DSC00628-HDR.jpg"
                  alt="Original living room with dated finishes"
                  className="w-full h-full object-cover aspect-[4/3]"
                />
                <div className="absolute top-4 left-4 bg-white/95 backdrop-blur-sm px-3 py-1.5 rounded-full">
                  <span className="text-xs font-semibold uppercase tracking-wider text-gray-700">Before</span>
                </div>
              </div>

              <div className="relative rounded-xl overflow-hidden shadow-lg group">
                <img
                  src="https://customer-assets.emergentagent.com/job_rcstudioapp/artifacts/zlncwc4j_Ai-Living.jpg"
                  alt="Virtually renovated living room with modern finishes"
                  className="w-full h-full object-cover aspect-[4/3]"
                />
                <div className="absolute top-4 left-4 bg-[#F5C056] px-3 py-1.5 rounded-full">
                  <span className="text-xs font-semibold uppercase tracking-wider text-gray-900">After</span>
                </div>
              </div>
            </div>

            {/* Content Grid */}
            <div className="grid lg:grid-cols-12 gap-8 items-start">
              <div className="lg:col-span-7 space-y-6">
                <div className="prose prose-gray max-w-none">
                  <p className="text-lg text-muted-foreground leading-relaxed">
                    Some properties need a little TLC before they photograph well. Outdated flooring, bold paint colors, or worn finishes can distract buyers from seeing the true potential of a space—and that often means listings get skipped online before they ever get a showing.
                  </p>
                  <p className="text-lg text-muted-foreground leading-relaxed">
                    Virtual staging and renovation visualization allows sellers and agents to show what a property could look like with simple cosmetic updates—new flooring, fresh paint, updated fixtures—without the cost or commitment of actual renovations.
                  </p>
                  <p className="text-lg text-muted-foreground leading-relaxed">
                    All virtually staged images are clearly labeled to maintain full transparency. This service is designed to help buyers visualize potential—not mislead.
                  </p>
                </div>
              </div>

              <div className="lg:col-span-5 bg-[#FAF9F6] rounded-2xl p-8 shadow-lg border border-gray-100">
                <div className="mb-6">
                  <div className="flex items-baseline gap-2 mb-1">
                    <p className="text-sm text-muted-foreground">Starting at</p>
                    <p className="text-3xl font-serif font-medium text-gray-900">$25</p>
                    <p className="text-sm text-muted-foreground">/ image</p>
                  </div>
                </div>

                <div className="h-px bg-gray-200 w-full mb-6"></div>

                <div className="space-y-4 mb-8">
                  {[
                    "Flooring replacement visualization",
                    "Wall color and paint changes",
                    "Fixture and finish updates",
                    "Same-day revision turnaround",
                    "Multiple options per room available",
                    "Projects of any size accepted",
                    "All images clearly labeled"
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#F5C056] mt-2"></div>
                      <span className="text-gray-600 text-sm leading-relaxed">{item}</span>
                    </div>
                  ))}
                </div>

                <div className="flex flex-col gap-3">
                  <Button className="w-full bg-[#050816] text-white hover:bg-[#050816]/90 rounded-lg h-11 text-xs uppercase tracking-widest font-medium transition-all shadow-md hover:shadow-lg">
                    Get a quote
                  </Button>
                  <Button variant="outline" className="w-full border-gray-300 text-gray-700 hover:bg-white hover:text-gray-900 hover:border-gray-400 rounded-lg h-11 text-xs uppercase tracking-widest font-medium transition-all bg-transparent">
                    View examples
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Back to Categories Footer */}
      <section className="py-12 bg-[#FAF9F6] border-t border-gray-200">
        <div className="container mx-auto px-6 text-center">
          <Link 
            to="/pricing" 
            className="inline-flex items-center gap-2 text-[#050816] hover:text-[#F5C056] font-medium transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to All Categories
          </Link>
        </div>
      </section>
    </div>
  );
};

export default PricingRealEstate;
